import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface ParentMissingField {
  key: string;
  label: string;
  fieldId: string;
}

const REQUIRED_PARENT_FIELDS: { key: string; label: string; fieldId: string }[] = [
  { key: "full_name", label: "Nom et prénom", fieldId: "fullName" },
  { key: "phone", label: "Numéro de téléphone", fieldId: "phone" },
  { key: "street_address", label: "Adresse postale", fieldId: "streetAddress" },
  { key: "city", label: "Ville", fieldId: "city" },
  { key: "bio", label: "Petite bio de présentation", fieldId: "bio" },
];

export interface ParentAccessState {
  status: "loading" | "not_parent" | "complete" | "incomplete";
  missingFields: ParentMissingField[];
}

/**
 * Bloque les fonctionnalités principales pour un parent dont le profil
 * n'a pas le minimum vital (nom, téléphone, adresse, ville, bio).
 */
export function useParentAccess(): ParentAccessState {
  const { user, role, loading: authLoading } = useAuth();
  const [refreshTick, setRefreshTick] = useState(0);
  const [state, setState] = useState<ParentAccessState>({
    status: "loading",
    missingFields: [],
  });

  useEffect(() => {
    const handler = () => setRefreshTick((t) => t + 1);
    window.addEventListener("parent-access:refresh", handler);
    // Also react to the existing pro-access refresh trigger fired after a profile save.
    window.addEventListener("pro-access:refresh", handler);
    return () => {
      window.removeEventListener("parent-access:refresh", handler);
      window.removeEventListener("pro-access:refresh", handler);
    };
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user || role !== "parent") {
      setState({ status: "not_parent", missingFields: [] });
      return;
    }

    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("full_name, city, bio")
        .eq("user_id", user.id)
        .maybeSingle<any>();

      const { data: phone } = await supabase.rpc("get_my_phone");
      const { data: priv } = await supabase.rpc("get_my_private_profile");
      const privRow = Array.isArray(priv) ? priv[0] : priv;

      const merged: Record<string, any> = {
        ...(data ?? {}),
        street_address: (privRow as any)?.street_address ?? null,
        phone: phone ?? null,
      };

      const missing = REQUIRED_PARENT_FIELDS.filter((f) => {
        const v = merged[f.key];
        return !v || (typeof v === "string" && !v.trim());
      });

      if (cancelled) return;
      setState({
        status: missing.length === 0 ? "complete" : "incomplete",
        missingFields: missing,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [user, role, authLoading, refreshTick]);

  return state;
}
